import { create } from 'zustand';
import type { SessionChild, SessionMembership, SessionUser } from '@saesak/shared';
import { api, apiData, facilityStore, tokenStore } from '@/lib/api';

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: SessionUser;
}

interface AuthState {
  user: SessionUser | null;
  /** 현재 보고 있는 시설 (§4 멀티테넌시) */
  membership: SessionMembership | null;
  status: 'idle' | 'loading' | 'authenticated' | 'anonymous';

  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<SessionUser>;
  /** 카카오 인가 코드 로그인 (§5.1 P5) */
  kakaoLogin: (code: string, redirectUri: string) => Promise<SessionUser>;
  register: (input: {
    email: string;
    password: string;
    name: string;
    verificationTicket: string;
    inviteCode?: string;
  }) => Promise<SessionUser>;
  redeemInvite: (code: string) => Promise<SessionUser>;
  switchFacility: (facilityId: string) => void;
  refreshSession: () => Promise<void>;
  logout: () => Promise<void>;
}

function pickMembership(user: SessionUser, preferredId?: string | null): SessionMembership | null {
  if (user.memberships.length === 0) return null;
  return (
    user.memberships.find((m) => m.facilityId === preferredId) ?? user.memberships[0]
  );
}

function applySession(user: SessionUser): { user: SessionUser; membership: SessionMembership | null } {
  const membership = pickMembership(user, facilityStore.get());
  if (membership) facilityStore.set(membership.facilityId);
  return { user, membership };
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  membership: null,
  status: 'idle',

  /** 앱 시작 시 저장된 토큰으로 세션을 복구한다. */
  async bootstrap() {
    if (!tokenStore.get()) {
      set({ status: 'anonymous' });
      return;
    }
    set({ status: 'loading' });
    try {
      const user = await apiData<SessionUser>('/auth/me');
      set({ ...applySession(user), status: 'authenticated' });
    } catch {
      tokenStore.clear();
      set({ user: null, membership: null, status: 'anonymous' });
    }
  },

  async login(email, password) {
    const { data } = await api<LoginResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    tokenStore.set({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    set({ ...applySession(data.user), status: 'authenticated' });
    return data.user;
  },

  async kakaoLogin(code, redirectUri) {
    const { data } = await api<LoginResponse>('/auth/kakao', {
      method: 'POST',
      body: { code, redirectUri },
    });
    tokenStore.set({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    set({ ...applySession(data.user), status: 'authenticated' });
    return data.user;
  },

  async register(input) {
    const { data } = await api<LoginResponse>('/auth/register', {
      method: 'POST',
      body: input,
    });
    tokenStore.set({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    set({ ...applySession(data.user), status: 'authenticated' });
    return data.user;
  },

  async redeemInvite(code) {
    const user = await apiData<SessionUser>('/auth/invites/redeem', {
      method: 'POST',
      body: { code },
    });
    set({ ...applySession(user), status: 'authenticated' });
    return user;
  },

  switchFacility(facilityId) {
    const user = get().user;
    if (!user) return;
    const membership = user.memberships.find((m) => m.facilityId === facilityId);
    if (!membership) return;
    facilityStore.set(facilityId);
    set({ membership });
  },

  async refreshSession() {
    const user = await apiData<SessionUser>('/auth/me');
    set(applySession(user));
  },

  async logout() {
    const tokens = tokenStore.get();
    try {
      await api('/auth/logout', {
        method: 'POST',
        body: { refreshToken: tokens?.refreshToken },
      });
    } catch {
      // 서버가 응답하지 않아도 로컬 세션은 끊는다.
    }
    tokenStore.clear();
    set({ user: null, membership: null, status: 'anonymous' });
  },
}));

/** 현재 시설에서 열람 가능한 자녀 (학부모 전용) */
export function useChildren(): SessionChild[] {
  const user = useAuth((s) => s.user);
  const membership = useAuth((s) => s.membership);
  if (!user || !membership) return [];
  return user.children.filter((c) => c.facilityId === membership.facilityId);
}

export function useRole(): SessionMembership['role'] | null {
  return useAuth((s) => s.membership?.role ?? null);
}

export function useCanWrite(): boolean {
  return useAuth((s) => s.membership?.canWrite ?? false);
}
